import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

/** Kleine, häufig gebrauchte Bausteine. */

export function cn(...values: (string | false | null | undefined)[]): string {
  return values.filter(Boolean).join(' ');
}

/**
 * Statusanzeige. Die Farbe steckt im Punkt, nicht in der Fläche —
 * so bleiben lange Listen ruhig lesbar.
 */
export function StatusPill({
  name,
  archived,
  className,
}: {
  name: string;
  archived?: boolean;
  className?: string;
}) {
  const variant = archived
    ? name.toLowerCase().includes('verkauf')
      ? 'pill-sold'
      : 'pill-archived'
    : name.toLowerCase().includes('lager')
      ? 'pill-stock'
      : 'pill-deployed';

  return <span className={cn('pill', variant, className)}>{name}</span>;
}

export function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="animate-fade-up mb-8 flex flex-wrap items-end justify-between gap-5">
      <div className="min-w-0">
        {eyebrow && <p className="eyebrow mb-1.5">{eyebrow}</p>}
        <h1 className="font-[family-name:var(--font-display)] text-4xl leading-tight text-[var(--color-fg)] sm:text-5xl">
          {title}
        </h1>
        {subtitle && <p className="mt-2 text-[var(--color-fg3)]">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
    </header>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="surface-quiet animate-fade-in flex flex-col items-center gap-4 px-8 py-20 text-center">
      {/* Ein angedeutetes Gewebe — das Leitmotiv, hier als leere Fläche. */}
      <div
        aria-hidden
        className="h-14 w-14 rounded-lg border border-[var(--color-line)] opacity-60"
        style={{
          backgroundImage:
            'repeating-linear-gradient(90deg, var(--color-khadi-forest-200) 0 1px, transparent 1px 6px), repeating-linear-gradient(0deg, var(--color-khadi-forest-200) 0 1px, transparent 1px 6px)',
        }}
      />
      <p className="font-[family-name:var(--font-display)] text-2xl">{title}</p>
      {description && <p className="max-w-lg text-[var(--color-fg3)]">{description}</p>}
      {action}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-[var(--color-fg3)]">
      <span
        aria-hidden
        className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--color-khadi-forest-200)] border-t-[var(--color-khadi-forest-600)]"
      />
      {label ?? 'Einen Moment …'}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
  return (
    <div
      role="alert"
      className="animate-fade-in rounded-[var(--radius-md)] border border-[var(--color-error)]/40 bg-[var(--color-khadi-clay-100)] px-5 py-4 text-sm text-[var(--color-khadi-clay-700)]"
    >
      {message}
    </div>
  );
}

/** Beschriftetes Wertepaar für Detailansichten. */
export function DataPoint({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="eyebrow mb-1.5">{label}</dt>
      <dd className="truncate text-lg text-[var(--color-fg1)]">{children ?? '—'}</dd>
    </div>
  );
}

/** Kennzahl fürs Dashboard. */
export function Stat({
  label,
  value,
  hint,
  to,
  search,
}: {
  label: string;
  value: number | string;
  hint?: string;
  to?: string;
  search?: Record<string, unknown>;
}) {
  const content = (
    <>
      <p className="eyebrow">{label}</p>
      <p className="tabular mt-2 font-[family-name:var(--font-display)] text-5xl leading-none text-[var(--color-fg)] sm:text-6xl">
        {value}
      </p>
      {hint && <p className="mt-2.5 text-sm text-[var(--color-fg3)]">{hint}</p>}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        search={search as never}
        className="surface card-link block px-6 py-6 no-underline"
      >
        {content}
      </Link>
    );
  }

  return <div className="surface px-6 py-6">{content}</div>;
}

/** Waagerechter Balken für Verteilungen (Kategorie, Standort, Status). */
export function BarRow({
  label,
  value,
  max,
  tone = 'forest',
}: {
  label: string;
  value: number;
  max: number;
  tone?: 'forest' | 'clay' | 'saffron';
}) {
  const percent = max > 0 ? Math.round((value / max) * 100) : 0;
  const color =
    tone === 'clay'
      ? 'var(--color-khadi-clay-500)'
      : tone === 'saffron'
        ? 'var(--color-khadi-saffron-400)'
        : 'var(--color-khadi-forest-500)';

  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1.5">
      <span className="truncate text-[var(--color-fg1)]">{label}</span>
      <span className="tabular text-lg font-semibold text-[var(--color-fg2)]">{value}</span>
      <div className="col-span-2 h-2.5 overflow-hidden rounded-full bg-[var(--color-khadi-cream-200)]">
        <div
          className="h-full rounded-full transition-[width] duration-700 ease-out"
          style={{ width: `${percent}%`, background: color }}
        />
      </div>
    </div>
  );
}
